import { useState } from "react";
import {
  ClipboardCheck, Plus, Check, X, ChevronDown, ChevronUp, Trash2
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "../ui/collapsible";

const TASK_CATEGORIES = [
  { value: "haccp", label: "HACCP Kontrolle", color: "text-red-500" },
  { value: "temperature", label: "Temperatur", color: "text-blue-500" },
  { value: "cleaning", label: "Reinigung", color: "text-cyan-500" },
  { value: "pest", label: "Schaedlingskontrolle", color: "text-orange-500" },
  { value: "storage", label: "Lagerung", color: "text-purple-500" },
  { value: "opening", label: "Oeffnung", color: "text-green-500" },
  { value: "closing", label: "Schliessung", color: "text-yellow-500" },
];

const ROLES = [
  { value: "all", label: "Alle Mitarbeiter" },
  { value: "service", label: "Service" },
  { value: "kitchen", label: "Kueche" },
  { value: "bar", label: "Bar" },
  { value: "cleaning", label: "Reinigung" },
  { value: "management", label: "Management" },
];

const FREQUENCIES = [
  { value: "daily", label: "Taeglich" },
  { value: "weekly", label: "Woechentlich" },
  { value: "monthly", label: "Monatlich" },
  { value: "once", label: "Einmalig" },
];

const ChecklistenTab = ({ checklists, getCategoryInfo, onDelete, onAdd }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [expandedChecklists, setExpandedChecklists] = useState({});
  const [newChecklistTask, setNewChecklistTask] = useState("");
  const [newChecklist, setNewChecklist] = useState({
    name: "", description: "", category: "haccp",
    assigned_role: "all", frequency: "daily", tasks: []
  });

  const toggleChecklist = (id) => {
    setExpandedChecklists(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleAddChecklistTask = () => {
    if (!newChecklistTask.trim()) return;
    setNewChecklist({
      ...newChecklist,
      tasks: [...newChecklist.tasks, { name: newChecklistTask, completed: false }]
    });
    setNewChecklistTask("");
  };

  const handleRemoveChecklistTask = (index) => {
    setNewChecklist({
      ...newChecklist,
      tasks: newChecklist.tasks.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = () => {
    if (!newChecklist.name.trim()) return;
    onAdd(newChecklist);
    setShowDialog(false);
    setNewChecklist({
      name: "", description: "", category: "haccp",
      assigned_role: "all", frequency: "daily", tasks: []
    });
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-white">Aktive Checklisten ({checklists.length})</h2>
        <Button onClick={() => setShowDialog(true)} className="bg-red-600 hover:bg-red-700 gap-2" data-testid="add-checklist-btn">
          <Plus size={16} /> Neue Checkliste
        </Button>
      </div>

      {checklists.length === 0 ? (
        <div className="text-center py-12 bg-slate-800/50 rounded-xl border border-slate-700">
          <ClipboardCheck size={48} className="mx-auto text-slate-600 mb-4" />
          <p className="text-slate-400 mb-4">Keine Checklisten vorhanden</p>
        </div>
      ) : (
        <div className="space-y-3">
          {checklists.map((checklist) => {
            const catInfo = getCategoryInfo(checklist.category);
            const CatIcon = catInfo.icon;
            const isExpanded = expandedChecklists[checklist.id];
            return (
              <Collapsible key={checklist.id} open={isExpanded} onOpenChange={() => toggleChecklist(checklist.id)}>
                <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
                  <CollapsibleTrigger asChild>
                    <button className="w-full flex items-center justify-between p-4 hover:bg-slate-700/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg bg-slate-700 ${catInfo.color}`}><CatIcon size={20} /></div>
                        <div className="text-left">
                          <h3 className="font-semibold text-white">{checklist.name}</h3>
                          <div className="flex items-center gap-2 text-xs text-slate-400">
                            <span className="capitalize">{checklist.frequency}</span>
                            <span>•</span>
                            <span>{checklist.tasks?.length || 0} Punkte</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500"
                          onClick={(e) => { e.stopPropagation(); onDelete(checklist.id); }}>
                          <Trash2 size={14} />
                        </Button>
                        {isExpanded ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
                      </div>
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="px-4 pb-4 border-t border-slate-700 pt-3">
                      {checklist.description && <p className="text-sm text-slate-400 mb-3">{checklist.description}</p>}
                      <div className="space-y-2">
                        {checklist.tasks?.map((task, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-2 bg-slate-700/50 rounded-lg">
                            <div className="w-5 h-5 rounded border-2 border-slate-500 flex items-center justify-center">
                              {task.completed && <Check size={12} className="text-green-500" />}
                            </div>
                            <span className="text-sm text-slate-300">{task.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            );
          })}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Neue Checkliste erstellen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Name</Label>
              <Input placeholder="z.B. Taegliche HACCP Kontrolle" value={newChecklist.name}
                onChange={(e) => setNewChecklist({ ...newChecklist, name: e.target.value })}
                className="bg-slate-700 border-slate-600" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Beschreibung</Label>
              <Textarea placeholder="Optional" value={newChecklist.description}
                onChange={(e) => setNewChecklist({ ...newChecklist, description: e.target.value })}
                className="bg-slate-700 border-slate-600" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Kategorie</Label>
                <Select value={newChecklist.category} onValueChange={(v) => setNewChecklist({ ...newChecklist, category: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                  <SelectContent>{TASK_CATEGORIES.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Haeufigkeit</Label>
                <Select value={newChecklist.frequency} onValueChange={(v) => setNewChecklist({ ...newChecklist, frequency: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                  <SelectContent>{FREQUENCIES.map((f) => (<SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Zugewiesen an</Label>
              <Select value={newChecklist.assigned_role} onValueChange={(v) => setNewChecklist({ ...newChecklist, assigned_role: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => (<SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Checkpunkte</Label>
              <div className="flex gap-2">
                <Input placeholder="Neuen Punkt hinzufuegen" value={newChecklistTask}
                  onChange={(e) => setNewChecklistTask(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddChecklistTask()}
                  className="bg-slate-700 border-slate-600" />
                <Button type="button" onClick={handleAddChecklistTask} variant="outline" className="border-slate-600"><Plus size={16} /></Button>
              </div>
              <div className="space-y-2 mt-2">
                {newChecklist.tasks.map((task, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-700 rounded-lg">
                    <span className="text-sm text-slate-300">{task.name}</span>
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => handleRemoveChecklistTask(idx)}><X size={12} /></Button>
                  </div>
                ))}
              </div>
            </div>
            <Button onClick={handleSubmit} className="w-full bg-red-600 hover:bg-red-700">Checkliste erstellen</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChecklistenTab;
